import React, { useState, useEffect, useMemo } from 'react';

import PropTypes from 'prop-types';
import _ from 'lodash';
import { Box, Button, FlexGrid, Notification, Select, StackView, TextInput, Typography } from '@telus-uds/components-web';

import { useLocalizedStringsContext } from '../../../../context/LocalizedStringsContext';

const AddSubscriberForm = ({
  isCorporate,
  rateplanList,
  renewalSubscriberList,
  setRenewalSubscriberList
}) => {
  const localizedStrings = useLocalizedStringsContext();
  const {
    contentfulValues: { value3year },
    portabilityTool: { labelNote },
    assigneeSRTModal: { addButton },
    iotOrderDetailsSection: {
      vehicleInfoMessage,
      vehicleVIN,
      vehicleMake,
      vehicleModel,
      vehicleYear,
      subscribersOfferList
    },
    ORDERS: { labelPleaseSelect }
  } = localizedStrings;

  const [dataField, setDataFields] = useState({
    subscriber: '',
    plan: '',
    device: '',
    term: '',
    price: '',
    offer: '',
    vin: '',
    make: '',
    model: '',
    year: ''
  });
  const [error, setError] = useState('');
  const rateplanModel = useMemo(() => {
    return rateplanList.reduce((acc, current) => {
      if (!_.get(current, 'fields.isHeader', false)) {
        const isRoaming = _.get(
          current,
          'fields.show50PercentUsCheckBox',
          false
        );
        const deviceList = _.get(current, 'fields.deviceList', []).reduce(
          (deviceAcc, deviceCurrent) => {
            const termList = _.get(deviceCurrent, 'fields.terms', []).reduce(
              (termsAcc, termsCurrent) => {
                const { termPrice, termPrice50PrecentUs } = _.get(
                  termsCurrent,
                  'fields',
                  {}
                );
                return {
                  ...termsAcc,
                  [_.get(termsCurrent, 'fields.termName', '')]: {
                    termPrice,
                    termPrice50PrecentUs
                  }
                };
              },
              {}
            );
            return {
              ...deviceAcc,
              [_.get(deviceCurrent, 'fields.deviceName', '')]: { ...termList }
            };
          },
          {}
        );

        return {
          ...acc,
          [_.get(current, 'fields.rateplanNameRenewal', '')]: { ...deviceList },
          ...(isRoaming && {
            [`${_.get(current, 'fields.rateplanNameRenewal', '')} - Roaming`]: {
              ...deviceList
            }
          })
        };
      }
      return { ...acc };
    }, {});
  }, [rateplanList]);

  const onAddVehicleInfo = () => {
    if (
      !dataField.subscriber ||
      !dataField.plan ||
      !dataField.device ||
      !dataField.term ||
      !dataField.price ||
      !dataField.offer ||
      !(dataField.vin || (dataField.make && dataField.model && dataField.year))
    ) {
      setError('Please enter all necessary information');
      return;
    }
    setRenewalSubscriberList([...renewalSubscriberList, dataField]);
    setDataFields({
      subscriber: '',
      plan: '',
      device: '',
      term: '',
      price: '',
      offer: '',
      vin: '',
      make: '',
      model: '',
      year: ''
    });
    setError('');
  };

  const getPlanList = () =>
    rateplanModel &&
    Object.keys(rateplanModel).reduce(
      (acc, current) => [...acc, { text: current, value: current }],
      []
    );

  const getDevicesList = () =>
    (dataField.plan &&
      Object.keys(_.get(rateplanModel, dataField.plan, {})).reduce(
        (acc, current) => [...acc, { text: current, value: current }],
        []
      )) ||
    [];

  const getTermList = () =>
    (dataField.device &&
      Object.keys(
        _.get(rateplanModel, `${dataField.plan}.${dataField.device}`, {})
      ).reduce((acc, current) => {
        const is3year = current === value3year;
        if (is3year) return [...acc, { text: current, value: current }];
        return [...acc];
      }, [])) ||
    [];

  const getPrice = () => {
    const termPriceKey = dataField.plan.includes('- Roaming')
      ? 'termPrice50PrecentUs'
      : 'termPrice';
    return (
      dataField.term &&
      _.get(
        rateplanModel,
        `${dataField.plan}.${dataField.device}.${dataField.term}.${termPriceKey}`,
        ''
      )
    );
  };

  useEffect(() => {
    if (dataField.term) setDataFields({ ...dataField, price: getPrice() });
  }, [dataField.term]);

  useEffect(() => {
    setDataFields({ ...dataField, device: '', term: '', price: '' });
  }, [dataField.plan]);

  useEffect(() => {
    setDataFields({ ...dataField, term: '', price: '' });
  }, [dataField.device]);

  return (
    <StackView space={3}>
      <Notification variant={{ style: 'subtle' }}>
        <Typography variant={{ bold: true }}>{labelNote}: </Typography>
        {vehicleInfoMessage}
      </Notification>
      <FlexGrid>
        <StackView space={4}>
          <FlexGrid.Row>
            <FlexGrid.Col>
              <TextInput
                label="Subscriber"
                value={dataField.subscriber}
                onChange={(value) =>
                  setDataFields({
                    ...dataField,
                    subscriber: value
                  })
                }
              />
            </FlexGrid.Col>
            <FlexGrid.Col>
              <Select
                disabled={!dataField.subscriber}
                label="Plan"
                placeholder={labelPleaseSelect}
                value={dataField.plan}
                onChange={(value) =>
                  setDataFields({
                    ...dataField,
                    plan: value
                  })
                }
              >
                {getPlanList() && getPlanList().map(option => (
                  <Select.Item key={option.value} value={option.value}>
                    {option.text}
                  </Select.Item>
                ))}
              </Select>
            </FlexGrid.Col>
            <FlexGrid.Col>
              <Select
                disabled={!dataField.plan}
                label="Devices"
                placeholder={labelPleaseSelect}
                value={dataField.device}
                onChange={(value) =>
                  setDataFields({
                    ...dataField,
                    device: value
                  })
                }
              >
                {getDevicesList() && getDevicesList().map(option => (
                  <Select.Item key={option.value} value={option.value}>
                    {option.text}
                  </Select.Item>
                ))}
              </Select>
            </FlexGrid.Col>
          </FlexGrid.Row>
          <FlexGrid.Row>
            <FlexGrid.Col>
              <Select
                disabled={!dataField.device}
                label="Term"
                placeholder={labelPleaseSelect}
                value={dataField.term}
                onChange={(value) =>
                  setDataFields({
                    ...dataField,
                    term: value
                  })
                }
              >
                {getTermList() && getTermList().map(option => (
                  <Select.Item key={option.value} value={option.value}>
                    {option.text}
                  </Select.Item>
                ))}
              </Select>
            </FlexGrid.Col>
            {!isCorporate && (
              <FlexGrid.Col>
                <TextInput
                  disabled
                  label="Price"
                  value={dataField.price}
                  onChange={(value) =>
                    setDataFields({
                      ...dataField,
                      price: value
                    })
                  }
                />
              </FlexGrid.Col>
            )}
            <FlexGrid.Col>
              <Select
                disabled={!dataField.price}
                label="Offer"
                placeholder={labelPleaseSelect}
                value={dataField.offer}
                onChange={(value) =>
                  setDataFields({
                    ...dataField,
                    offer: value
                  })
                }
              >
                {subscribersOfferList && subscribersOfferList.map(option => (
                  <Select.Item key={option.value || option.text} value={option.value || option.text}>
                    {option.text || option.value}
                  </Select.Item>
                ))}
              </Select>
            </FlexGrid.Col>
          </FlexGrid.Row>
          <FlexGrid.Row>
            <FlexGrid.Col>
              <TextInput
                disabled={dataField.make || dataField.model || dataField.year}
                label={vehicleVIN}
                value={dataField.vin}
                onChange={(value) =>
                  setDataFields({
                    ...dataField,
                    vin: value
                  })
                }
              />
            </FlexGrid.Col>
            <FlexGrid.Col>
              <TextInput
                disabled={dataField.vin}
                label={vehicleMake}
                value={dataField.make}
                onChange={(value) =>
                  setDataFields({
                    ...dataField,
                    make: value
                  })
                }
              />
            </FlexGrid.Col>
            <FlexGrid.Col>
              <TextInput
                disabled={dataField.vin}
                label={vehicleModel}
                value={dataField.model}
                onChange={(value) =>
                  setDataFields({
                    ...dataField,
                    model: value
                  })
                }
              />
            </FlexGrid.Col>
          </FlexGrid.Row>
          <FlexGrid.Row horizontalAlign="start">
            <FlexGrid.Col xs={4} md={4}>
              <TextInput
                disabled={dataField.vin}
                label={vehicleYear}
                pattern="[0-9]{4}"
                maxLength="4"
                value={dataField.year}
                onChange={(value) =>
                  setDataFields({
                    ...dataField,
                    year: value
                  })
                }
              />
            </FlexGrid.Col>
          </FlexGrid.Row>
        </StackView>
      </FlexGrid>
      <Box style={{ padding: '1.5rem' }}>
        <Button onPress={onAddVehicleInfo}>{addButton}</Button>
      </Box>
      {error && (
        <Notification
          variant={{ validation: 'error' }}
          dismissible
          onExit={() => setError('')}
          copy="en"
        >
          {error}
        </Notification>
      )}
    </StackView>
  );
};

AddSubscriberForm.propTypes = {
  isCorporate: PropTypes.bool.isRequired,
  rateplanList: PropTypes.objectOf(PropTypes.any).isRequired,
  renewalSubscriberList: PropTypes.arrayOf(PropTypes.object).isRequired,
  setRenewalSubscriberList: PropTypes.func.isRequired
};

export default AddSubscriberForm;
